/* eslint-disable max-len */
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Sprite } from '@pixi/sprite';

import { Shared } from '../../App';
import { NineSliceButton } from '../../ui/buttons/NineSliceButton';
import { SpineDebugComponent } from '../components/SpineDebugComponent';
import { VehicleEntity } from '../entities/VehicleEntity';
import { Game } from '../Game';
import { PhysicsDrawSystem } from '../physics';

export class SpineDebugSystem implements SystemInterface
{
    public game: Game;
    public vehicles: VehicleEntity[] = [];
    public offset = 0;
    public static GAP = 250;
    public axis = { x: 0, y: 0 };
    private _exportBtn: NineSliceButton;
    private _animationBtn: NineSliceButton;
    private _animationIndex = 0;
    private _optionsBtn: NineSliceButton;
    private _optionsOpened = false;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.game.app.data.getAvailableCharacters().forEach(this._setupCharacter.bind(this));

        const exportIcon = Sprite.from(Shared.assets.getTexture('icon-updown-arrow'));

        this._exportBtn = new NineSliceButton({
            icon: exportIcon,
            accessibleTitle: 'Back',
            w: exportIcon.width * 1.5,
            h: exportIcon.height * 1.5,
        });
        this.game.view25d.container.addChild(this._exportBtn.view);
        this._exportBtn.onPress.connect(() => this._exportValues());

        const animationIcon = Sprite.from(Shared.assets.getTexture('video-ad'));

        this._animationBtn = new NineSliceButton({
            icon: animationIcon,
            accessibleTitle: 'Back',
            w: animationIcon.width * 1.5,
            h: animationIcon.height * 1.5,
        });
        this.game.view25d.container.addChild(this._animationBtn.view);
        this._animationBtn.onPress.connect(() => this._updateAnimation());

        const garageIcon = Sprite.from(Shared.assets.getTexture('icon-shop'));

        this._optionsBtn = new NineSliceButton({
            icon: garageIcon,
            accessibleTitle: 'Back',
            w: garageIcon.width * 1.5,
            h: garageIcon.height * 1.5,
        });
        this.game.view25d.container.addChild(this._optionsBtn.view);
        this._optionsBtn.onPress.connect(() =>
        {
            if (this._optionsOpened)
            {
                this.game.app.controller.screens.overlay.goto('empty');
                this._optionsOpened = false;

                return;
            }

            this.game.app.controller.toRideStudio();
            this._optionsOpened = true;
        });

        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    private _setupCharacter(characterId: string, index: number): void
    {
        const vehicle = new VehicleEntity();

        vehicle.build({ ...this.game.garage.user.data.vehicle, character: characterId });
        vehicle.selfAddToGame();
        vehicle.trailLeft.snap();
        vehicle.trailRight.snap();
        vehicle.reset();
        vehicle.respawn(800 + (index * SpineDebugSystem.GAP), -80, 0, false);
        vehicle.motor.turnOff();
        vehicle.motor.idleRun();
        vehicle.character.spine.playCelebrate();
        if(vehicle.tag){
            vehicle.tag.turnOff();
        }

        this.vehicles.push(vehicle);
        if (!this.game.vehicle) this.game.vehicle = vehicle;
    }

    public updateVehicles(): void
    {
        const data = this.game.garage.user.data.vehicle;

        this.vehicles.forEach((vehicle, index) =>
        {
            vehicle.build({ ...data, character: this.game.app.data.getAvailableCharacters()[index] });
            vehicle.updateColor(
                data.colorChassis,
                data.colorWheelLeft,
                data.colorWheelRight,
            );
        });

        const drawSystem = this.game.getSystem(PhysicsDrawSystem as any) as PhysicsDrawSystem;

        if (drawSystem) drawSystem.cleanup();
    }

    public update(): void
    {
        this.offset += this.axis.y * 5;
        this.offset = Math.min(Math.max(this.offset, 0), (this.vehicles.length - 1) * SpineDebugSystem.GAP);
    }

    private _updateAnimation(): void
    {
        this._animationIndex++;
        this._animationIndex %= 3;

        this.vehicles.forEach((vehicle) =>
        {
            switch (this._animationIndex)
            {
                case 0:
                    vehicle.character.spine.playCelebrate();
                    break;
                case 1:
                    vehicle.character.spine.playIdle();
                    break;
                case 2:
                    vehicle.character.spine.playFall();
                    break;
            }
        });
    }

    private async _exportValues(): Promise<void>
    {
        if (!window.showDirectoryPicker) return;
        const dirHandle = await window.showDirectoryPicker();

        this.vehicles.forEach(async (vehicle) =>
        {
            const fileHandle = await dirHandle.getFileHandle(`character-${vehicle.data.character}.json`, { create: true });
            const writable = await fileHandle.createWritable();
            const sdc = vehicle.getComponent(SpineDebugComponent);

            await writable.write(JSON.stringify(sdc.data, null, 4));
            await writable.close();
        });
    }

    public resize(w: number, h: number): void
    {
        this._exportBtn.view.x = 70 - (w / 2);
        this._exportBtn.view.y = (h / 2) - 70;
        this._animationBtn.view.y = (h / 2) - 70;
        this._optionsBtn.view.x = (w / 2) - 70;
        this._optionsBtn.view.y = (h / 2) - 70;
    }

    private handleKeyDown = (e: KeyboardEvent) =>
    {
        if (e.repeat) return;
        if (e.key === 'ArrowUp' || e.key === 'w')
        {
            this.axis.x = 1;
        }
        if (e.key === 'ArrowDown' || e.key === 's')
        {
            this.axis.x = -1;
        }
        if (e.key === 'ArrowLeft' || e.key === 'a') this.axis.y = -1;
        if (e.key === 'ArrowRight' || e.key === 'd') this.axis.y = 1;
    };

    private handleKeyUp = (e: KeyboardEvent) =>
    {
        if ((e.key === 'ArrowUp' || e.key === 'w') && this.axis.x === 1)
        {
            this.axis.x = 0;
        }
        if ((e.key === 'ArrowDown' || e.key === 's') && this.axis.x === -1)
        {
            this.axis.x = 0;
        }
        if ((e.key === 'ArrowLeft' || e.key === 'a') && this.axis.y === -1) this.axis.y = 0;
        if ((e.key === 'ArrowRight' || e.key === 'd') && this.axis.y === 1) this.axis.y = 0;
    };
}
